// Post-processing effects of the colour pipeline: linear exposure before tone mapping and the per-map
// grade after it (S-curve contrast, vibrance-weighted saturation, split-tone). Both live in the
// same EffectPass as tone mapping, so they cost no extra pass.

import * as THREE from "three";
import { BlendFunction, Effect } from "postprocessing";
import type { MapGrade } from "./mapTypes";

const EXPOSURE_FRAGMENT = /* glsl */ `
uniform float exposure;
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  outputColor = vec4(inputColor.rgb * exposure, inputColor.a);
}
`;

// Works on tone-mapped linear colour. sqrt() stands in for the sRGB curve so the S-curve and the
// saturation act on perceptual values; black and white stay fixed so clean whites stay clean.
const GRADE_FRAGMENT = /* glsl */ `
uniform float contrast;
uniform float saturation;
uniform vec3 shadowTint;
uniform vec3 highlightTint;
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = sqrt(clamp(inputColor.rgb, 0.0, 1.0));
  float l = dot(c, LUMA);
  c = mix(c, c * c * (3.0 - 2.0 * c), contrast);
  float chroma = max(max(c.r, c.g), c.b) - min(min(c.r, c.g), c.b);
  c = mix(vec3(dot(c, LUMA)), c, 1.0 + (saturation - 1.0) * (1.0 - 0.5 * chroma));
  vec3 shadowRatio = shadowTint / max(dot(shadowTint, LUMA), 1e-3);
  vec3 highlightRatio = highlightTint / max(dot(highlightTint, LUMA), 1e-3);
  c *= mix(vec3(1.0), shadowRatio, 0.16 * (1.0 - l) * (1.0 - l));
  c *= mix(vec3(1.0), highlightRatio, 0.12 * l * l);
  outputColor = vec4(c * c, inputColor.a);
}
`;

export class ExposureEffect extends Effect {
  constructor(exposure = 1) {
    super("ExposureEffect", EXPOSURE_FRAGMENT, {
      blendFunction: BlendFunction.SET,
      uniforms: new Map<string, THREE.Uniform>([["exposure", new THREE.Uniform(exposure)]]),
    });
  }

  set exposure(value: number) {
    (this.uniforms.get("exposure") as THREE.Uniform<number>).value = value;
  }
}

export class GradeEffect extends Effect {
  constructor(grade: MapGrade) {
    super("GradeEffect", GRADE_FRAGMENT, {
      blendFunction: BlendFunction.SET,
      uniforms: new Map<string, THREE.Uniform>([
        ["contrast", new THREE.Uniform(0)],
        ["saturation", new THREE.Uniform(1)],
        ["shadowTint", new THREE.Uniform(new THREE.Color())],
        ["highlightTint", new THREE.Uniform(new THREE.Color())],
      ]),
    });
    this.setGrade(grade);
  }

  setGrade(grade: MapGrade): void {
    const u = this.uniforms;
    (u.get("contrast") as THREE.Uniform<number>).value = grade.contrast;
    (u.get("saturation") as THREE.Uniform<number>).value = grade.saturation;
    // The tints are used as raw sRGB numbers (the shader works on perceptual values): no colour management.
    (u.get("shadowTint") as THREE.Uniform<THREE.Color>).value.setStyle(grade.shadowTint, THREE.LinearSRGBColorSpace);
    (u.get("highlightTint") as THREE.Uniform<THREE.Color>).value.setStyle(grade.highlightTint, THREE.LinearSRGBColorSpace);
  }
}
